import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CategoryMeta,
  CategoryMetaFlat,
  CreateCategoryPayload,
  CreateItemPayload,
  DropdownItem,
  ModuleNode,
  UpdateCategoryPayload,
  UpdateItemPayload,
} from "../types";

const KEY = ["dropdowns"] as const;

export function useDropdownTree(): UseQueryResult<ModuleNode[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...KEY, "tree", tokenKey],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ tree: ModuleNode[] }>("/dropdowns/tree", {
        token: accessToken,
      });
      return res.tree;
    },
  });
}

export function useTreeMetadata(): UseQueryResult<CategoryMetaFlat[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...KEY, "tree-metadata", tokenKey],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ categories: CategoryMetaFlat[] }>(
        "/dropdowns/tree/metadata",
        { token: accessToken },
      );
      return res.categories;
    },
  });
}

export function useCategoryItems(
  category: string | undefined,
  parentValue?: string,
  metadataFilters?: Record<string, string>,
): UseQueryResult<DropdownItem[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [
      ...KEY,
      "items",
      tokenKey,
      category,
      parentValue ?? null,
      metadataFilters ?? null,
    ],
    enabled: Boolean(category) && !!accessToken,
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (parentValue) query.parentValue = parentValue;
      if (metadataFilters) {
        for (const [k, v] of Object.entries(metadataFilters)) {
          query[`metadata.${k}`] = v;
        }
      }
      const res = await apiFetch<{ items: DropdownItem[] }>(
        `/dropdowns/${category}`,
        {
          token: accessToken,
          query: Object.keys(query).length > 0 ? query : undefined,
        },
      );
      return res.items;
    },
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: CreateCategoryPayload) => {
      const res = await apiFetch<{ category: CategoryMeta }>(
        "/dropdowns/categories",
        { method: "POST", body: payload, token: accessToken },
      );
      return res.category;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateCategory(category: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: UpdateCategoryPayload) => {
      const res = await apiFetch<{ category: CategoryMeta }>(
        `/dropdowns/categories/${category}`,
        { method: "PUT", body: payload, token: accessToken },
      );
      return res.category;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useCreateItem(category: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: CreateItemPayload) => {
      const res = await apiFetch<{ item: DropdownItem }>(
        `/dropdowns/${category}/items`,
        { method: "POST", body: payload, token: accessToken },
      );
      return res.item;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateItem() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateItemPayload;
    }) => {
      const res = await apiFetch<{ item: DropdownItem }>(
        `/dropdowns/items/${id}`,
        { method: "PUT", body: payload, token: accessToken },
      );
      return res.item;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteItem() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ success: true }>(`/dropdowns/items/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useReorderItems() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (items: { id: string; sortOrder: number }[]) =>
      apiFetch<{ success: true }>("/dropdowns/reorder", {
        method: "POST",
        body: { items },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
